import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';

import ProtectedRoute from '@/components/auth/ProtectedRoute';
import DashboardLayout from '@/components/layouts/AdminLayout';
import Companies from '@/pages/company';
import HelpdeskAdminPage from '@/pages/content-management/helpdesk/manage';
import NotificationsPage from '@/pages/notifications';

// Lazy load all components
// Auth
const Login = lazy(() => import('@/pages/auth/Login'));
const Register = lazy(() => import('@/pages/auth/Register'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'));
const TwoFactorAuth = lazy(() => import('@/pages/auth/TwoFactorAuth'));
const BackupCodes = lazy(() => import('@/pages/auth/BackupCodes'));
const OAuthCallback = lazy(() => import('@/pages/auth/OAuthCallback'));

// Dashboard
const Dashboard = lazy(() => import('@/pages/dashboard'));

// Settings
const Account = lazy(() => import('@/components/settings/Account'));
const SecuritySettings = lazy(() => import('@/components/settings/tabs/SecurityTab'));
const NotificationSettings = lazy(() => import('@/components/settings/tabs/NotificationTab'));
const CompanyUsers = lazy(() => import('@/pages/company/Users'));
const CompanyProfile = lazy(() => import('@/pages/company/Profile'));

// Account Management
const CustomersPage = lazy(() => import('@/pages/account-management/customers'));
const CustomerDetailsPage = lazy(() => import('@/pages/account-management/customers/[id]'));
const BrandsPage = lazy(() => import('@/pages/account-management/brands'));
const BoostSupplierPage = lazy(() => import('@/pages/account-management/boost-your-supplier'));
const UploadCertificatesPage = lazy(() => import('@/pages/account-management/upload-certificates'));

// Product Management
const PSImpactScorePage = lazy(() => import('@/pages/product-management/ps-impact-score'));
const MaximaPage = lazy(() => import('@/pages/product-management/maxima'));
const ImagesPage = lazy(() => import('@/pages/product-management/images'));
const ExcelImportPage = lazy(() => import('@/pages/product-management/excel-import'));
const DataEntryPage = lazy(() => import('@/pages/data-entry'));
const PSImpactScoreDetailPage = lazy(() => import('@/pages/product-management/ps-impact-score/[id]'));

// Data Integration
const GS1ImportLogPage = lazy(() => import('@/pages/data-integration/gs1-import-log'));
const AssortmentIdentificationPage = lazy(() => import('@/pages/data-integration/assortment-identification'));

// Reports
const ReportsPage = lazy(() => import('@/pages/reports'));

// Maintenance
const MaintenancePage = lazy(() => import('@/pages/maintenance'));

// Content Management
//const PermalinkManagerPage = lazy(() => import('@/pages/content-management/permalink'));
const PermalinkGeneratorPage = lazy(() => import('@/pages/content-management/permalink/generator'));
const PermalinkStatisticsPage = lazy(() => import('@/pages/content-management/permalink/statistics'));
const DigitalCatalogPage = lazy(() => import('@/pages/content-management/digital-catalog'));
const MessageTemplatesPage = lazy(() => import('@/pages/content-management/message-template'));
const HelpdeskPage = lazy(() => import('@/pages/content-management/helpdesk'));

// Loading component
const LoadingSpinner = () => (
    <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
    </div>
);

// Wrapper component for lazy loaded routes
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
    <Suspense fallback={<LoadingSpinner />}>
        {children}
    </Suspense>
);

export const routes: RouteObject[] = [
    {
        path: '/',
        element: <LazyRoute><ProtectedRoute><DashboardLayout /></ProtectedRoute></LazyRoute>,
        children: [
            {
                index: true,
                element: <LazyRoute><Dashboard /></LazyRoute>
            },
            {
                path: 'settings',
                children: [
                    {
                        path: 'account',
                        element: <LazyRoute><Account /></LazyRoute>
                    },
                    {
                        path: 'security',
                        element: <LazyRoute><SecuritySettings /></LazyRoute>
                    },
                    {
                        path: 'notifications',
                        element: <LazyRoute><NotificationSettings /></LazyRoute>
                    }
                ]
            },
            {
                path: 'account-management',
                children: [
                    {
                        path: 'customers',
                        children: [
                            {
                                index: true,
                                element: <LazyRoute><CustomersPage /></LazyRoute>
                            },
                            {
                                path: ':id',
                                element: <LazyRoute><CustomerDetailsPage /></LazyRoute>
                            }
                        ]
                    },
                    {
                        path: 'brands',
                        element: <LazyRoute><BrandsPage /></LazyRoute>
                    },
                    {
                        path: 'boost-your-supplier',
                        element: <LazyRoute><BoostSupplierPage /></LazyRoute>
                    },
                    {
                        path: 'upload-certificates',
                        element: <LazyRoute><UploadCertificatesPage /></LazyRoute>
                    }
                ]
            },
            {
                path: 'product-management',
                children: [
                    {
                        path: 'ps-impact-score',
                        element: <LazyRoute><PSImpactScorePage /></LazyRoute>
                    },
                    {
                        path: 'ps-impact-score/:id',
                        element: <LazyRoute><PSImpactScoreDetailPage /></LazyRoute>
                    },
                    {
                        path: 'maxima',
                        element: <LazyRoute><MaximaPage /></LazyRoute>
                    },
                    {
                        path: 'images',
                        element: <LazyRoute><ImagesPage /></LazyRoute>
                    },
                    {
                        path: 'excel-import',
                        element: <LazyRoute><ExcelImportPage /></LazyRoute>
                    }
                ]
            },
            {
                path: 'data-entry',
                element: <LazyRoute><DataEntryPage /></LazyRoute>
            },
            {
                path: 'data-integration',
                children: [
                    {
                        path: 'gs1-import-log',
                        element: <LazyRoute><GS1ImportLogPage /></LazyRoute>
                    },
                    {
                        path: 'assortment-identification',
                        element: <LazyRoute><AssortmentIdentificationPage /></LazyRoute>
                    }
                ]
            },
            {
                path: 'reports',
                element: <LazyRoute><ReportsPage /></LazyRoute>
            },
            {
                path: 'company',
                children: [
                    {
                        path: 'profile',
                        element: <LazyRoute><CompanyProfile /></LazyRoute>
                    },
                    {
                        path: 'users',
                        element: <LazyRoute><CompanyUsers /></LazyRoute>
                    }
                ]
            },
            {
                path: 'companies',
                element: <LazyRoute><Companies /></LazyRoute>
            },
            {
                path: 'maintenance',
                element: <LazyRoute><MaintenancePage /></LazyRoute>
            },
            {
                path: 'content-management',
                children: [
                    {
                        path: 'permalink',
                        children: [
                            {
                                index: true,
                                element: <LazyRoute><PermalinkGeneratorPage /></LazyRoute>
                            },
                            {
                                path: 'statistics',
                                element: <LazyRoute><PermalinkStatisticsPage /></LazyRoute>
                            }
                        ]
                    },
                    {
                        path: 'digital-catalog',
                        element: <LazyRoute><DigitalCatalogPage /></LazyRoute>
                    },
                    {
                        path: 'message-template',
                        element: <LazyRoute><MessageTemplatesPage /></LazyRoute>
                    },
                    {
                        path: 'helpdesk',
                        element: <LazyRoute><HelpdeskPage /></LazyRoute>
                    },
                    {
                        path: 'helpdesk-admin',
                        element: <LazyRoute><HelpdeskAdminPage /></LazyRoute>
                    }
                ]
            },
            {
                path: 'notifications',
                element: <LazyRoute><NotificationsPage /></LazyRoute>
            }
        ]
    },
    {
        path: '/auth',
        children: [
            {
                path: 'login',
                element: <LazyRoute><Login /></LazyRoute>
            },
            {
                path: 'register',
                element: <LazyRoute><Register /></LazyRoute>
            },
            {
                path: 'forgot-password',
                element: <LazyRoute><ForgotPassword /></LazyRoute>
            },
            {
                path: 'reset-password',
                element: <LazyRoute><ResetPassword /></LazyRoute>
            },
            {
                path: '2fa-verify',
                element: <LazyRoute><TwoFactorAuth /></LazyRoute>
            },
            {
                path: 'backup-codes',
                element: <LazyRoute><BackupCodes /></LazyRoute>
            },
            {
                path: 'oauth-callback',
                element: <LazyRoute><OAuthCallback /></LazyRoute>
            }
        ]
    }
]; 